/* Backend exceptions, translated into something a player can act on.

Every typed exception the server raises is a rule the player just ran into. They arrived as
`{action: "error", message: "NoActionsRemaining: ..."}` and got console.warn'd, so the player saw
nothing at all and had no way to tell a rule from a bug.

Keys are the exception class names; the server sends "ClassName" or "ClassName: detail".

English lives HERE, not in `en.json`: the backend's `test_every_refusal_has_player_wording.py`
regex-parses this file for `  ExceptionName:` lines, so this object is the contract with the server
and the English source of truth. Spanish (and any other language) lives in the locale files under
`errors.<ExceptionName>`; `playerFacingError` looks the key up there and falls back to this map.

One thing deliberately absent: basic training refuses off-script actions with names of its own
(`errors.Training*`, both locales), and those are **not** here. That guard also runs the other way —
a key in this file naming no exception the backend raises fails it — and a tutorial that answers in
this tab raises nothing. `playerFacingError` reads the locale first, so they word themselves
without this map hearing about them. */
import i18n from "src/i18n"
import { answersAutoAdvance } from "src/utils/rounds"

const MESSAGES = {
  // Actions and the round
  NoActionsRemaining: "You have used all your actions this round.",
  UserAlreadyReady: "You are ready for this round — un-ready to keep acting.",
  GameNotAbleToGoNextRound: "The round is not over yet.",
  StaleRound: "That round has already been played.",
  NotTheHost: "Only the host can advance a round that still has time.",
  GamePaused: "The game is paused — press resume to continue.",
  FogPreviewUnavailable: "Fog of war is on — every view is already the real one.",
  InvalidFormation: "That formation does not fit the battlefield.",
  GameNotPaused: "The game is not paused.",
  NoPausesLeft: "You have used every pause this game gives you.",
  // A frame the server could not read (security audit, 2026-10-25): a client sending nonsense,
  // which a player only meets from a client older or newer than the server.
  MalformedMessage: "The server could not read that — reload the page and try again.",
  // gamekit 0.11.0's socket pace (`GAMEKIT_WS_RATE`): the actions past the bucket were dropped,
  // so the player has to send the last one again, not just wait.
  TooManyMessages: "Too many actions at once — the last ones were dropped; wait a moment and send them again.",
  HostStillActive: "The host is still here, or their round is not overdue yet.",
  AlreadyHost: "You already hold the clock.",
  NotInGame: "You are not a player in this game.",
  GameAlreadyFinished: "This game is over.",

  // Movement
  InvalidPath: "That route does not work — moves must be orthogonal and unbroken.",
  PathTooLong: "That march is too long.",
  InsufficientUnits: "You do not have that many units there.",
  InsufficientFoodForTrip: "Not enough food to provision that march — the trip is paid at departure.",
  BuildingAlreadyUpgrading: "A level of that building is already ordered this round. It lands when the round turns.",
  SettlementAlreadyOrdered: "A colony is already ordered on that cell this round.",
  ResourcesCommittedToConstruction: "That is already promised to a building or a colony ordered this round.",
  NoUpgradeToCancel: "There is no level ordered on that building to cancel.",
  NoFoundingToCancel: "There is no colony ordered on that cell to cancel.",
  NoMovementToCancel: "That march is already on the road: only a march ordered this round can be cancelled.",
  NoStrikeToCancel: "There is no missile of yours in the air there to cancel.",
  NoTunnelOrderToCancel: "There is no tunnel order of yours to cancel.",
  UnitsCommittedToTunnel: "Those units are already ordered into or out of the tunnel this round.",
  UnitsCommittedToDischarge: "Those units are already ordered to stand down this round — they become workers when the round turns.",
  NoDischargeToCancel: "There is no disband order of yours on that cell to cancel.",
  CannotSettleWhileFighting: "You cannot found a settlement while the ground is still being fought over — win it first.",
  CannotMarchFromACombat: "Those troops are in a fight — they hold the cell or they fall, but they do not march out of it.",
  InvalidLocation: "That location is not valid.",

  // Production and buildings
  BuildingNotOperational: "That building is not built yet.",
  InsufficientWorkers: "Not enough workers.",
  InsufficientMeat: "Not enough meat.",
  InsufficientFood: "Not enough food.",
  InvalidUnitType: "Your race cannot field that unit.",
  InvalidUnitCount: "That is not a number of units you can send.",
  TooManyUnits: "That is far more units than any order can hold.",
  InvalidAmount: "That is not an amount you can send.",
  InvalidBuildingType: "That building does not exist.",
  CellNotOwned: "That cell is not yours.",
  WorkersCannotBeDischarged: "Workers cannot be disbanded.",

  // Abilities
  FriendlyFire: "Missiles never land on your own side's cities.",
  MissileProgramRequired: "Research the missile program before building or firing a silo.",
  TunnelProgramRequired: "Research the tunnel program before building or using a Tunnel.",
  CellUnderSiege: "That city is in combat — it builds and deploys nothing until the fight ends.",
  NoTunnelAvailable: "That city has no Tunnel to load or unload through — it needs one of yours, built.",
  TunnelCapacityExceeded: "That many will not fit into the tunnel in one load — raise the Tunnel's level.",
  TunnelFull: "The tunnel is full.",
  NotEnoughUnitsInTunnel: "The tunnel does not hold that many of those.",
  InvalidRoundLength: "A round lasts between 15 seconds and 10 minutes.",
  InvalidMapSize: "That map size does not exist.",
  InvalidRankedSetting: "Ranked is either on or off.",
  LobbySettingsLocked: "Somebody is ready — the settings stay as they are until everybody un-readies.",
  GoogleAlreadyLinked: "This account is already linked to a Google account.",
  GoogleAccountTaken: "That Google account is already linked to another player.",
  GoogleNotLinked: "This account is not linked to a Google account.",
  GoogleUnlinkWouldLockYouOut: "Set a password before unlinking Google, or you would be locked out.",
  SiloOnCooldown: "The silo is still reloading.",
  TargetOutOfRange: "That target is out of range.",
  NoSiloAvailable: "No missile silo is ready there.",
  WrongTeamForAction: "Your side cannot do that.",
  NotAnAlly: "That player is not on your team.",
  InsufficientResources: "You do not have that much to give.",

  // Research
  InsufficientResearchPoints: "Not enough research points.",
  ResearchMaxedOut: "That research is already at its maximum.",
  ResearchRequiresProgram: "Research your side's program first.",
  // Two real exceptions, one for each gate: `InvalidResearchType` when the name is not in your
  // team's enum at all, `InvalidResearch` when it is but is not yours to buy. Only the second was
  // listed, so the common case fell through to the generic message.
  InvalidResearchType: "Your side cannot research that.",
  InvalidResearch: "Your side cannot research that.",

  // Expansion
  CellNotClaimable: "You cannot settle there.",
  NoPresenceOnCell: "You need troops standing there first.",

  // Lobby. Requirement 16 covered the game and left this out, and the lobby is where a player
  // spends their first minutes: joining, picking a side, choosing where to start. Every refusal
  // here used to arrive as nothing at all.
  LobbyNotFoundError: "That lobby no longer exists.",
  NoAvailableLobby: "No open lobby to join right now.",
  LobbyFullException: "That lobby is full.",
  LobbyIncorrectPassword: "Wrong password for that lobby.",
  LobbyIsNotReady: "Everyone has to be ready before the game can start.",
  RankedRoomsSeatEveryone: "A ranked game has no watchers: everybody in the room takes a seat, leaves, or the room goes unranked.",
  OnlyHostCanStartGame: "Only the host can start the game.",
  UserAlreadyInLobbyError: "You are already in this lobby.",
  OnlyHostCanRemoveMembers: "Only the host can remove somebody from the room.",
  CannotRemoveHost: "The host cannot be removed: leave the room instead.",
  UserNotInLobbyException: "You are not in that lobby.",
  InvalidTeamException: "That team does not exist.",
  UserHasNoTeam: "Pick a side first.",
  CanNotSetReadyWithoutTeam: "Pick a side before readying up.",
  CanNotSetReadyWithoutLocations: "Choose all your starting cells before readying up.",
  CanNotSetReadyWithoutRace: "Pick a race before readying up.",
  UserNotReady: "You are not ready yet.",
  InvalidRace: "That race does not exist for your side.",
  InvalidUnitIcon: "That colour is not one your side can play.",
  UnitIconAlreadyTaken: "Somebody on your side is already playing that colour.",
  TeamColoursClash: "Two players on a side are wearing the same colour — everyone needs their own.",
  UserHasAlreadyTheRaceAssigned: "That is already your race.",
  LocationOccupied: "Somebody else has taken that cell.",
  MaxLocationsAssigned: "You have already chosen all your starting cells.",
  InvalidCoordinates: "You cannot start there — pick land inside the map.",
  OnlyHostCanManageBots: "Only the host can add or remove bots.",
  InvalidBotDifficulty: "That bot difficulty does not exist.",
  NoBotAvailable: "Every bot of that class and difficulty is already in this lobby.",

  // Survival (requirement 28). Everybody plays one side and the bots are the other, so the rules
  // a player runs into here are all about a seat that does not exist.
  SurvivalIsOneSide: "In survival everybody plays the same side — the bots are the other one.",
  SurvivalSeatsPeopleOnly: "Survival seats people only — the enemy arrives in waves.",
  NotASurvivalLobby: "That setting belongs to a survival game.",
  SpectatorsWatch: "You are spectating — only players act.",

  // Chat (security audit, 2026-10-25).
  ChatMessageNotText: "Only text can be sent in the chat.",
  ChatMessageTooLong: "That message is too long — shorten it and send it again.",

  // Credits. The shop itself is REST and answers with written sentences, so only the two rules a
  // player runs into *in the lobby* are worded here — picking a class they have not bought, and
  // readying up for a game they cannot pay the entry fee for.
  InsufficientCredits: "Not enough credits — claim your daily credits or visit the shop.",
  RaceNotUnlocked: "You have not unlocked that class yet — buy it in the shop.",

  // Looks. The shop is the only screen that buys or wears one, and it only ever offers the button
  // the look allows — so every one of these means the catalogue on screen is older than the
  // server's, which is a sentence a player can act on ("reopen the shop") rather than a rule.
  UnknownCosmetic: "That look is no longer available.",
  CosmeticNotForSale: "That look comes with every account — there is nothing to buy.",
  CosmeticAlreadyOwned: "You already own that look.",
  CosmeticNotOwned: "You have not bought that look yet.",
  CosmeticNotWearable: "That one is sent, not worn.",

  // Your name. REST like the shop, but worded here rather than left to the server's sentence: this
  // is the one refusal a player meets while typing, and reading it in English on a Spanish profile
  // is reading it in the wrong language at the worst moment. `UsernameTooSoon` interpolates the
  // date the server sends with it — a fixed "not yet" leaves a player retrying tomorrow.
  UsernameTaken: "That name is taken — try another.",
  UsernameTooSoon: "You can change your name again on {{date}}.",
  UsernameLockedWhileInPlay: "Leave your lobby or finish your game before changing your name.",
  UsernameUnchanged: "That is already your name.",

  // Closing an account. The one refusal it has, and it is the same shape as the rename's: seats
  // are other people's game in progress, and the way out is one screen away.
  AccountLockedWhileInPlay: "Leave your lobby or finish your game before deleting your account.",

  // Playing as a guest (2026-10-02). The shop refuses a guest server-side; saving the account is
  // the way through, and the other two are the save's own refusals.
  GuestsCannotShop: "Save your account to use the shop.",
  WithdrawalWaiverRequired: "Tick the immediate-delivery box to buy.",
  NotAGuest: "This account is already saved.",
  EmailTaken: "That email belongs to another account.",

  // Reactions. Two of these mean the catalogue on screen is older than the server's; the third is
  // the cooldown, which is the only one a player meets by doing nothing wrong.
  UnknownReaction: "That reaction is no longer available.",
  ReactionNotOwned: "You have not bought that reaction yet.",
  ReactingTooFast: "One at a time — give it a second.",
  // gamekit's own refusals (the framework under this game).
  BotsNotAllowed: "This room takes no bots.",
  CanNotSetReadyWithoutSeat: "Join a team before you get ready.",
  GameInProgress: "This room's game is still being played.",
  InvalidRoomSetting: "That setting is not one of the choices.",
  LobbyPasswordTooLong: "That password is too long.",
  NoSeat: "Join a team first.",
  NotABot: "That seat is not a bot.",
  NotAPlayer: "You are not playing in this game.",
  NotAnAvatar: "That one is not a picture.",
  OnlyHostCanChangeSettings: "Only the host can change the room's settings.",
  ProductAlreadyOwned: "You already own that one.",
  ProductNoLongerSold: "That one is no longer sold; whoever has it keeps it.",
  ProductNotForSale: "That one is free — every account already has it.",
  ProductNotOwned: "You do not own that one yet — buy it in the shop.",
  UnknownProduct: "That one does not exist.",
  UnknownRoomSetting: "This room has no such setting.",
}

/* `values` are interpolated into the sentence, in whichever language it is read in. Only the
   refusals that carry data need it — the cooldown's date — and passing none leaves every other
   message exactly as it was. */
/* The one refusal that is not a message for the player. `StaleRound` is the server saying "the
   round you meant to turn is already turned": a no-op by design, and since any seated player may
   turn an overdue round (2026-09-22), a host whose background tab woke late will hear it now and
   then. It keeps its wording above because the backend's guard test wants every refusal worded;
   the socket thunk asks this before toasting. */
export const isSilentRefusal = (rawMessage) => {
  const [name] = String(rawMessage ?? "").split(":")
  const key = name.trim()
  // And the answer to this browser's own automatic round turn (see `answersAutoAdvance`).
  return key === "StaleRound" || answersAutoAdvance(key)
}

export const playerFacingError = (rawMessage, values = {}) => {
  if (!rawMessage) return i18n.t("errors.somethingWentWrong", { defaultValue: "Something went wrong." })
  const [name] = String(rawMessage).split(":")
  const key = name.trim()
  return i18n.t(`errors.${key}`, {
    ...values,
    defaultValue: MESSAGES[key] ?? i18n.t("errors.notAllowed", { defaultValue: "That action was not allowed." }),
  })
}

export default MESSAGES
